import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { StatCard } from '../../components/ui/StatCard'
import { Avatar } from '../../components/ui/Avatar'
import { Badge } from '../../components/ui/Badge'
import { BarChart } from '../../components/charts/BarChart'
import { ScoreGauge } from '../../components/charts/ScoreGauge'
import { EmptyState } from '../../components/ui/EmptyState'
import styles from './ManagerDashboard.module.css'

interface DatiDashboard {
  team: { id: string; nome: string }
  totale_membri: number
  membri_attivi_oggi: number
  sessioni_team_settimana: number
  sessioni_team_mese: number
  minuti_allenamento_mese: number
  score_medio_team: number
  migliore_sdr: { utente: { nome: string; cognome: string }; score_medio: number } | null
  sdr_da_supportare: { utente: { nome: string; cognome: string }; score_medio: number } | null
  lead_piu_usato: { nome: string; ruolo: string } | null
  andamento_settimanale: Array<{ settimana: string; sessioni: number; score_medio: number }>
}

export function ManagerDashboard() {
  const q = useQuery({
    queryKey: ['manager', 'dashboard'],
    queryFn: async () => (await api.get<{ dati: DatiDashboard }>('/manager/dashboard')).data.dati,
  })

  if (q.isLoading) return <div className={styles.skeleton}>Caricamento…</div>
  if (q.error || !q.data) {
    return (
      <EmptyState
        titolo="Impossibile caricare la dashboard team"
        descrizione="Verifica di avere un team associato all'account."
      />
    )
  }

  const d = q.data
  return (
    <div className={styles.wrap}>
      <div className={styles.intestazione}>
        <h2>{d.team.nome}</h2>
        <Badge variante="info">{d.totale_membri} membri</Badge>
      </div>

      <div className={styles.kpi}>
        <StatCard etichetta="Membri attivi oggi" valore={d.membri_attivi_oggi} unita={`/${d.totale_membri}`} />
        <StatCard etichetta="Sessioni settimana" valore={d.sessioni_team_settimana} />
        <StatCard etichetta="Sessioni mese" valore={d.sessioni_team_mese} />
        <StatCard etichetta="Minuti totali" valore={d.minuti_allenamento_mese} unita="min" />
      </div>

      <div className={styles.contenuto}>
        <div className={styles.colSinistra}>
          <Card titolo="Andamento settimanale">
            {d.andamento_settimanale.length > 0 ? (
              <BarChart
                dati={d.andamento_settimanale}
                chiaveX="settimana"
                chiaveY="sessioni"
              />
            ) : (
              <EmptyState titolo="Nessun dato" />
            )}
          </Card>

          <div className={styles.cardCoppia}>
            <Card titolo="🏆 Migliore SDR">
              {d.migliore_sdr ? (
                <div className={styles.sdrCella}>
                  <Avatar
                    nome={d.migliore_sdr.utente.nome}
                    cognome={d.migliore_sdr.utente.cognome}
                  />
                  <div className={styles.sdrDati}>
                    <div className={styles.sdrNome}>
                      {d.migliore_sdr.utente.nome} {d.migliore_sdr.utente.cognome}
                    </div>
                    <div className={styles.sdrScore}>
                      Voto medio: <strong>{d.migliore_sdr.score_medio.toFixed(1)}</strong>/10
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState titolo="Nessun dato" />
              )}
            </Card>

            <Card titolo="⚠️ Da supportare">
              {d.sdr_da_supportare ? (
                <div className={styles.sdrCella}>
                  <Avatar
                    nome={d.sdr_da_supportare.utente.nome}
                    cognome={d.sdr_da_supportare.utente.cognome}
                  />
                  <div className={styles.sdrDati}>
                    <div className={styles.sdrNome}>
                      {d.sdr_da_supportare.utente.nome} {d.sdr_da_supportare.utente.cognome}
                    </div>
                    <div className={styles.sdrScore}>
                      Voto medio: <strong>{d.sdr_da_supportare.score_medio.toFixed(1)}</strong>/10
                    </div>
                  </div>
                </div>
              ) : (
                <EmptyState titolo="Tutti vanno bene" />
              )}
            </Card>
          </div>
        </div>

        <div className={styles.colDestra}>
          <Card titolo="Voto medio team">
            <div className={styles.gaugeWrap}>
              <ScoreGauge voto={d.score_medio_team} etichetta="su 10" />
            </div>
          </Card>

          <Card titolo="Lead più allenato">
            {d.lead_piu_usato ? (
              <div className={styles.leadInfo}>
                <div className={styles.leadNome}>{d.lead_piu_usato.nome}</div>
                <div className={styles.leadRuolo}>{d.lead_piu_usato.ruolo}</div>
              </div>
            ) : (
              <EmptyState titolo="Nessun lead usato" />
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
